import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Trophy, TrendingUp, Target, Handshake, Minus, ChevronUp, ChevronDown, Minus as MinusIcon, History } from "lucide-react";
import type { Bot } from "@shared/schema";

const STYLE_LABELS: Record<string, string> = {
  aggressive: "Агрессивный",
  cooperative: "Кооперативный",
  balanced: "Сбалансированный",
  manipulative: "Манипулятивный",
  analytical: "Аналитический",
};

interface RatingEntry {
  matchType: string;
  matchId: string;
  opponentId: string;
  opponentName: string;
  scoreFor: number;
  scoreAgainst: number;
  result: "win" | "loss" | "draw";
  delta: number;
  ratingAfter: number;
  date?: string;
}

interface RatingHistory {
  botId: string;
  botName: string;
  currentRating: number;
  history: RatingEntry[];
}

function RatingHistoryDialog({ botId, botName, open, onClose }: { botId: string; botName: string; open: boolean; onClose: () => void }) {
  const { data, isLoading } = useQuery<RatingHistory>({
    queryKey: ["/api/bots", botId, "rating-history"],
    staleTime: 0,
    enabled: open,
  });

  // Custom queryFn for nested path
  const { data: historyData, isLoading: histLoading } = useQuery<RatingHistory>({
    queryKey: ["rating-history", botId],
    queryFn: async () => {
      const res = await apiRequest("GET", `/api/bots/${botId}/rating-history`);
      return res.json();
    },
    staleTime: 0,
    enabled: open,
  });

  const history = historyData?.history || [];
  const loading = histLoading;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <History className="w-4 h-4" />
            История рейтинга — {botName}
          </DialogTitle>
        </DialogHeader>
        {historyData && (
          <div className="text-center mb-2">
            <span className="text-2xl font-bold text-primary">{historyData.currentRating}</span>
            <span className="text-sm text-muted-foreground ml-2">ELO</span>
          </div>
        )}
        <ScrollArea className="h-[400px]">
          {loading ? (
            <div className="space-y-2">
              {[1, 2, 3].map(i => <div key={i} className="h-12 bg-muted rounded animate-pulse" />)}
            </div>
          ) : history.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground text-sm">
              Нет записей о поединках
            </div>
          ) : (
            <div className="space-y-2">
              {history.map((entry, i) => (
                <Card key={i} className="p-3 flex items-center gap-3">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold shrink-0 ${
                    entry.result === "win" ? "bg-green-500/10 text-green-400" :
                    entry.result === "loss" ? "bg-red-500/10 text-red-400" :
                    "bg-muted text-muted-foreground"
                  }`}>
                    {entry.result === "win" ? <ChevronUp className="w-4 h-4" /> :
                     entry.result === "loss" ? <ChevronDown className="w-4 h-4" /> :
                     <MinusIcon className="w-4 h-4" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium">
                      vs {entry.opponentName}
                    </div>
                    <div className="text-xs text-muted-foreground flex items-center gap-2">
                      <span>{entry.scoreFor} : {entry.scoreAgainst}</span>
                      <Badge variant="outline" className="text-xs px-1 py-0">
                        {entry.matchType === "duel" ? "арена" : "внешний"}
                      </Badge>
                      {entry.date && <span>{new Date(entry.date).toLocaleDateString("ru-RU")}</span>}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className={`text-sm font-bold ${
                      entry.delta > 0 ? "text-green-400" : entry.delta < 0 ? "text-red-400" : "text-muted-foreground"
                    }`}>
                      {entry.delta > 0 ? "+" : ""}{entry.delta}
                    </div>
                    <div className="text-xs text-muted-foreground">{entry.ratingAfter} ELO</div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}

export default function LeaderboardPage() {
  const { data: bots = [], isLoading } = useQuery<Bot[]>({ queryKey: ["/api/bots"] });
  const [selectedBot, setSelectedBot] = useState<{ id: string; name: string } | null>(null);

  const sorted = [...bots].sort((a, b) => b.rating - a.rating);

  const getMedal = (i: number) => {
    if (i === 0) return "🥇";
    if (i === 1) return "🥈";
    if (i === 2) return "🥉";
    return `${i + 1}.`;
  };

  const getWinRate = (bot: Bot) => {
    const total = bot.wins + bot.losses + bot.draws;
    if (total === 0) return 0;
    return Math.round((bot.wins / total) * 100);
  };

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <div className="mb-6">
        <h1 className="text-xl font-semibold flex items-center gap-2" data-testid="text-leaderboard-title">
          <Trophy className="w-5 h-5 text-primary" />
          Рейтинг ботов-переговорщиков
        </h1>
        <p className="text-muted-foreground text-sm mt-1">
          ELO-рейтинг обновляется после каждого поединка. Нажмите на участника для просмотра истории.
        </p>
      </div>

      {selectedBot && (
        <RatingHistoryDialog
          botId={selectedBot.id}
          botName={selectedBot.name}
          open={true}
          onClose={() => setSelectedBot(null)}
        />
      )}

      {isLoading ? (
        <div className="space-y-3">
          {[1, 2, 3].map(i => (
            <Card key={i} className="p-4 animate-pulse">
              <div className="h-4 bg-muted rounded w-1/3" />
            </Card>
          ))}
        </div>
      ) : (
        <div className="space-y-2">
          {sorted.map((bot, i) => {
            const total = bot.wins + bot.losses + bot.draws;
            const winRate = getWinRate(bot);
            return (
              <Card
                key={bot.id}
                className={`p-4 flex items-center gap-4 transition-all cursor-pointer hover:border-primary/30 ${
                  i === 0 ? "border-primary/30 glow-amber" : ""
                }`}
                onClick={() => setSelectedBot({ id: bot.id, name: bot.name })}
                data-testid={`leaderboard-row-${bot.id}`}
              >
                <span className="text-lg w-8 text-center font-semibold">{getMedal(i)}</span>
                <span className="text-2xl">{bot.avatar}</span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold text-sm truncate">{bot.name}</h3>
                    <Badge variant="outline" className="text-xs shrink-0">
                      {STYLE_LABELS[bot.style] || bot.style}
                    </Badge>
                  </div>
                  <p className="text-xs text-muted-foreground truncate">{bot.description}</p>
                </div>
                <div className="flex items-center gap-6 shrink-0">
                  <div className="text-center">
                    <div className="text-lg font-bold text-primary">{bot.rating}</div>
                    <span className="text-xs text-muted-foreground">ELO</span>
                  </div>
                  <div className="hidden sm:flex items-center gap-3 text-xs text-muted-foreground">
                    <span className="flex items-center gap-1 text-green-400">
                      <Target className="w-3 h-3" />
                      {bot.wins}
                    </span>
                    <span className="flex items-center gap-1 text-red-400">
                      <Minus className="w-3 h-3" />
                      {bot.losses}
                    </span>
                    <span className="flex items-center gap-1">
                      <Handshake className="w-3 h-3" />
                      {bot.draws}
                    </span>
                  </div>
                  <div className="hidden md:block text-center min-w-[60px]">
                    <div className="flex items-center gap-1">
                      <TrendingUp className="w-3 h-3" />
                      <span className="text-sm font-medium">{winRate}%</span>
                    </div>
                    <span className="text-xs text-muted-foreground">{total} игр</span>
                  </div>
                  <History className="w-4 h-4 text-muted-foreground opacity-0 group-hover:opacity-100" />
                </div>
              </Card>
            );
          })}

          {sorted.length === 0 && (
            <div className="text-center py-12 text-muted-foreground">
              <p className="text-sm">Нет ботов для отображения рейтинга</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
